import { computed, ComputedRef, Ref } from 'vue'

import { TeamSide } from '@collector/shared-domain'
import {
  CricketSubParticipantStatId,
  EventsLineupsParticipant,
  Participant,
} from '@collector/sportsapi-client-legacy'
import { EventSettingsType } from '@mobile/database/EventSettingsRepository'

import { useEventInjections } from '../../Shared/RelationDependencies/useEventInjections'
import { useEventSettings } from '../../useEventSettings'
import { useEventSubParticipants } from '../../useEventSubParticipants'
import { useCricketConfiguration } from './CricketRelationDependencies/useCricketConfiguration'
import {
  findLineupSubParticipantBasedOnId,
  findLineupSubParticipantBasedOnStat,
} from './LiveView/CurrentBatters/utils'

export function useCricketCurrentBattersAndBowler(side: Ref<TeamSide>): {
  currentMainBatter: ComputedRef<EventsLineupsParticipant | undefined>
  currentBowler: ComputedRef<EventsLineupsParticipant | undefined>
  currentSecondaryBatter: ComputedRef<EventsLineupsParticipant | undefined>
  shouldRenderCurrentBatters: (participant: Participant) => boolean
} {
  // composable
  const { probableHomeLineups, probableAwayLineups } = useEventInjections()
  const { activeParticipant } = useCricketConfiguration()
  const { eventSettings } = useEventSettings(EventSettingsType.MOBILE)
  const { eventSubParticipants } = useEventSubParticipants()

  // data
  const currentParticipantLineups = computed(() =>
    side.value === 'home' ? probableHomeLineups : probableAwayLineups,
  )

  // computed
  const currentMainBatter = computed(() => {
    return findLineupSubParticipantBasedOnStat(
      CricketSubParticipantStatId.CurrentBatter,
      eventSubParticipants.value,
      currentParticipantLineups.value.value,
    )
  })

  const currentSecondaryBatter = computed(() => {
    return eventSettings.value.currentSelectedCachedBatterId
      ? findLineupSubParticipantBasedOnId(
          eventSettings.value.currentSelectedCachedBatterId,
          currentParticipantLineups.value.value,
        )
      : undefined
  })

  const currentBowler = computed(() => {
    return findLineupSubParticipantBasedOnStat(
      CricketSubParticipantStatId.CurrentBowler,
      eventSubParticipants.value,
      currentParticipantLineups.value.value,
    )
  })

  // methods
  function shouldRenderCurrentBatters(participant: Participant): boolean {
    return (
      (activeParticipant.value.participant &&
        activeParticipant.value.participant.id === participant.id) ||
      false
    )
  }

  return {
    currentMainBatter,
    currentBowler,
    currentSecondaryBatter,
    shouldRenderCurrentBatters,
  }
}
